/**
 * Harmonic Analysis & Chord Detection Engine
 * Integrates real-time MIDI chords and recorded DAW tracks into Chord Notator lead sheets
 */

import { MidiNote, TimeSignature, DetectedChord } from '../types/midi';
import { getTicksPerMeasure } from './timing';
import { formatChordNotatorChord } from '../../../utils/audio';

export const PITCH_NAMES_SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const PITCH_NAMES_FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

// Suffixes whose Chord Notator spelling differs from this detector's internal `suffix`
// (kept internally for chordSymbolToPitches/audition matching against CHORD_DEFINITIONS).
const SUFFIX_DISPLAY_ALIASES: Record<string, string> = {
  aug: '+',
  m7b5: 'ø7',
};

// Builds the user-facing symbol in Chord Notator's own grammar (same function the
// grid editor uses), so detected chords and hand-written chords are never two dialects.
function toChordNotatorSymbol(root: string, suffix: string, slash: string): string {
  const displaySuffix = SUFFIX_DISPLAY_ALIASES[suffix] ?? suffix;
  return formatChordNotatorChord(`${root}${displaySuffix}${slash}`);
}

interface ChordDefinition {
  type: string;
  quality: string;
  intervals: number[]; // Relative to root in semitones (0..11)
  suffix: string;
  priority: number;
}

const CHORD_DEFINITIONS: ChordDefinition[] = [
  // Common Triads (Highest priority for clean lead sheet readouts)
  { type: '', quality: 'major', intervals: [0, 4, 7], suffix: '', priority: 10 },
  { type: 'm', quality: 'minor', intervals: [0, 3, 7], suffix: 'm', priority: 10 },
  { type: 'dim', quality: 'diminished', intervals: [0, 3, 6], suffix: 'dim', priority: 8 },
  { type: 'aug', quality: 'augmented', intervals: [0, 4, 8], suffix: 'aug', priority: 8 },
  { type: 'sus4', quality: 'suspended', intervals: [0, 5, 7], suffix: 'sus4', priority: 9 },
  { type: 'sus2', quality: 'suspended', intervals: [0, 2, 7], suffix: 'sus2', priority: 9 },

  // Seventh Chords
  { type: 'maj7', quality: 'major-seventh', intervals: [0, 4, 7, 11], suffix: 'maj7', priority: 9 },
  { type: '7', quality: 'dominant-seventh', intervals: [0, 4, 7, 10], suffix: '7', priority: 9 },
  { type: 'm7', quality: 'minor-seventh', intervals: [0, 3, 7, 10], suffix: 'm7', priority: 9 },
  { type: 'm7b5', quality: 'half-diminished', intervals: [0, 3, 6, 10], suffix: 'm7b5', priority: 8 },
  { type: 'dim7', quality: 'diminished-seventh', intervals: [0, 3, 6, 9], suffix: 'dim7', priority: 8 },
  { type: 'm(maj7)', quality: 'minor-major-seventh', intervals: [0, 3, 7, 11], suffix: 'm(maj7)', priority: 7 },
  { type: '7sus4', quality: 'seventh-suspended-fourth', intervals: [0, 5, 7, 10], suffix: '7sus4', priority: 8 },

  // Sixths & Added Tones
  { type: '6', quality: 'sixth', intervals: [0, 4, 7, 9], suffix: '6', priority: 7 },
  { type: 'm6', quality: 'minor-sixth', intervals: [0, 3, 7, 9], suffix: 'm6', priority: 7 },
  { type: 'add9', quality: 'add9', intervals: [0, 2, 4, 7], suffix: 'add9', priority: 8 },
  { type: '9', quality: 'ninth', intervals: [0, 2, 4, 7, 10], suffix: '9', priority: 7 },
  { type: 'maj9', quality: 'major-ninth', intervals: [0, 2, 4, 7, 11], suffix: 'maj9', priority: 7 },
  { type: 'm9', quality: 'minor-ninth', intervals: [0, 2, 3, 7, 10], suffix: 'm9', priority: 7 },

  // Fifth / Power chord
  { type: '5', quality: 'fifth', intervals: [0, 7], suffix: '5', priority: 6 },
];

/**
 * Detect chord from an instantaneous set of active MIDI pitches (e.g. from live keyboard)
 */
export function detectRealtimeChord(activePitches: number[], useFlats: boolean = false): DetectedChord | null {
  if (!activePitches || activePitches.length === 0) return null;
  const pitchNames = useFlats ? PITCH_NAMES_FLAT : PITCH_NAMES_SHARP;

  const sorted = Array.from(new Set(activePitches)).sort((a, b) => a - b);
  const bassPitch = sorted[0];
  const bassClass = bassPitch % 12;
  const pitchClasses = Array.from(new Set(sorted.map((p) => p % 12)));

  if (pitchClasses.length === 1) {
    const singleName = pitchNames[pitchClasses[0]];
    return {
      symbol: formatChordNotatorChord(singleName),
      root: singleName,
      suffix: '',
      bass: singleName,
      pitches: sorted,
      confidence: 1,
    };
  }

  let bestMatch: DetectedChord | null = null;
  let bestScore = -100;

  for (const root of pitchClasses) {
    const rootName = pitchNames[root];
    const relIntervals = pitchClasses.map((p) => (p - root + 12) % 12);

    for (const def of CHORD_DEFINITIONS) {
      const matchCount = def.intervals.filter((iv) => relIntervals.includes(iv)).length;
      const extraCount = relIntervals.filter((iv) => !def.intervals.includes(iv)).length;
      const missingCount = def.intervals.length - matchCount;

      if (matchCount >= 2 && missingCount <= 1) {
        let score = (matchCount * 12) - (extraCount * 6) - (missingCount * 8) + def.priority;
        if (root === bassClass) score += 8; // root position bonus
        if (matchCount === def.intervals.length && extraCount === 0) score += 25; // exact triad or 7th

        if (score > bestScore) {
          bestScore = score;

          // Inversion (slash chord) detection:
          // If the lowest sounding pitch (bass) is different from the chord root,
          // note it as an inverted chord (e.g. F/A, F/C, C/E, G/B, Am/C).
          let slash = '';
          let bassName = pitchNames[root];
          if (bassClass !== root) {
            bassName = pitchNames[bassClass];
            slash = `/${bassName}`;
          }

          bestMatch = {
            symbol: toChordNotatorSymbol(rootName, def.suffix, slash),
            root: rootName,
            suffix: def.suffix,
            bass: bassName,
            pitches: sorted,
            confidence: Math.max(0.5, Math.min(1.0, (score + 20) / 60)),
          };
        }
      }
    }
  }

  return bestMatch;
}

/**
 * Detect chord from a temporal window of MIDI notes (e.g. 1 measure of music)
 * Uses duration & velocity weighting to distinguish accompaniment chords from passing melody notes.
 */
export function detectChordFromNotes(
  notes: MidiNote[],
  measureStartTick: number = 0,
  useFlats: boolean = false
): DetectedChord | null {
  if (!notes || notes.length === 0) return null;
  const pitchNames = useFlats ? PITCH_NAMES_FLAT : PITCH_NAMES_SHARP;

  // Weight accumulator for all 12 pitch classes
  const weights = new Array<number>(12).fill(0);

  // Identify true physical lowest pitch in this temporal chord
  let lowestNote: MidiNote | null = null;

  for (const note of notes) {
    const pc = note.pitch % 12;
    // Downbeat or on-beat notes receive slight harmonic weighting
    const isDownbeat = Math.abs((note.startTick - measureStartTick) % 480) < 60;
    const beatMultiplier = isDownbeat ? 1.3 : 1.0;
    const weight = Math.max(120, note.durationTicks) * (note.velocity / 100) * beatMultiplier;

    weights[pc] += weight;

    if (!lowestNote || note.pitch < lowestNote.pitch) {
      lowestNote = note;
    }
  }

  const maxWeight = Math.max(...weights);
  if (maxWeight <= 0) return null;

  const threshold = maxWeight * 0.18;
  const significantPitchClasses = weights
    .map((w, idx) => ({ pc: idx, w }))
    .filter((item) => item.w >= threshold)
    .sort((a, b) => b.w - a.w)
    .map((item) => item.pc);

  if (significantPitchClasses.length === 0) return null;

  const physicalBassPitch = lowestNote ? lowestNote.pitch : 60;
  const physicalBassClass = physicalBassPitch % 12;

  if (significantPitchClasses.length === 1) {
    const name = pitchNames[significantPitchClasses[0]];
    return {
      symbol: formatChordNotatorChord(name),
      root: name,
      suffix: '',
      bass: name,
      pitches: notes.map((n) => n.pitch),
      confidence: 0.9,
    };
  }

  let bestMatch: DetectedChord | null = null;
  let bestScore = -100;

  for (const root of significantPitchClasses) {
    const rootName = pitchNames[root];
    const relIntervals = significantPitchClasses.map((p) => (p - root + 12) % 12);

    for (const def of CHORD_DEFINITIONS) {
      const matchCount = def.intervals.filter((iv) => relIntervals.includes(iv)).length;
      const extraCount = relIntervals.filter((iv) => !def.intervals.includes(iv)).length;
      const missingCount = def.intervals.length - matchCount;

      if (matchCount >= 2 && missingCount <= 1) {
        // Calculate harmonic score
        let score = (matchCount * 14) - (extraCount * 6) - (missingCount * 8) + def.priority;

        // Weight bonus for root presence
        const rootWeightRatio = weights[root] / maxWeight;
        score += rootWeightRatio * 8;

        if (root === physicalBassClass) {
          score += 10; // root position bonus
        }

        if (matchCount === def.intervals.length && extraCount === 0) {
          score += 25; // exact match
        }

        if (score > bestScore) {
          bestScore = score;

          // Inversion (slash chord) detection:
          // If the lowest sounding pitch (bass) is different from the chord root,
          // note it as an inverted chord (e.g. F/A, F/C, C/E, G/B, Am/C).
          let slash = '';
          let bassName = pitchNames[root];
          if (physicalBassClass !== root) {
            bassName = pitchNames[physicalBassClass];
            slash = `/${bassName}`;
          }

          bestMatch = {
            symbol: toChordNotatorSymbol(rootName, def.suffix, slash),
            root: rootName,
            suffix: def.suffix,
            bass: bassName,
            pitches: notes.map((n) => n.pitch),
            confidence: Math.max(0.4, Math.min(1.0, (score + 20) / 60)),
          };
        }
      }
    }
  }

  return bestMatch;
}

/**
 * Scan an entire MIDI track and analyze chords for every measure in the song.
 * Supports multiple distinct chords per measure (e.g. 2, 3 or 4 chords on beats).
 * Merges consecutive duplicate chord comping and snaps chord boundaries to musical beats.
 */
export function detectAllMeasureChords(
  notes: MidiNote[],
  timeSignature: TimeSignature,
  ppq: number,
  totalMeasures: number,
  useFlats: boolean = false
): Map<number, DetectedChord[]> {
  const result = new Map<number, DetectedChord[]>();
  const measureTicks = getTicksPerMeasure(timeSignature, ppq);
  const beatTicks = Math.round(measureTicks / timeSignature.beats);

  for (let m = 0; m < totalMeasures; m++) {
    const measureStartTick = m * measureTicks;
    const measureEndTick = (m + 1) * measureTicks;

    // Filter notes that sound in this measure
    const notesInMeasure = notes.filter((n) => {
      const noteEnd = n.startTick + n.durationTicks;
      return (
        (n.startTick >= measureStartTick && n.startTick < measureEndTick) ||
        (n.startTick < measureStartTick && noteEnd > measureStartTick + beatTicks * 0.5)
      );
    });

    if (notesInMeasure.length === 0) continue;

    // Find all distinct note onset times within this measure
    const rawOnsets = notesInMeasure
      .filter((n) => n.startTick >= measureStartTick && n.startTick < measureEndTick)
      .map((n) => n.startTick)
      .sort((a, b) => a - b);

    // If no notes start in this measure (only sustaining notes from prior bar)
    if (rawOnsets.length === 0) {
      const chord = detectChordFromNotes(notesInMeasure, measureStartTick, useFlats);
      if (chord) {
        chord.startTick = measureStartTick;
        chord.durationTicks = measureTicks;
        result.set(m, [chord]);
      }
      continue;
    }

    // Cluster onsets within ~140 ticks (accommodates human hand strumming / unquantized spread)
    const clusterOnsets: number[] = [];
    for (const tick of rawOnsets) {
      if (clusterOnsets.length === 0) {
        clusterOnsets.push(tick);
      } else {
        const last = clusterOnsets[clusterOnsets.length - 1];
        if (tick - last > 140) {
          clusterOnsets.push(tick);
        }
      }
    }

    const detectedInMeasure: DetectedChord[] = [];

    for (let cIdx = 0; cIdx < clusterOnsets.length; cIdx++) {
      const cTick = clusterOnsets[cIdx];
      const nextCTick = cIdx < clusterOnsets.length - 1 ? clusterOnsets[cIdx + 1] : measureEndTick;

      // Notes active in this temporal chord slice
      const sliceNotes = notesInMeasure.filter((n) => {
        const nEnd = n.startTick + n.durationTicks;
        return (
          (Math.abs(n.startTick - cTick) <= 140) ||
          (n.startTick < cTick && nEnd > cTick + 80)
        );
      });

      if (sliceNotes.length === 0) continue;

      const chord = detectChordFromNotes(sliceNotes, cTick, useFlats);
      if (!chord) continue;

      // Only treat as a distinct harmonic event if at least 2 distinct pitch classes
      // or if it's the very first chord of the bar
      const distinctPcs = new Set(sliceNotes.map((n) => n.pitch % 12));
      if (distinctPcs.size < 2 && detectedInMeasure.length > 0) {
        // Melodic passing single note: extend previous chord duration instead of replacing
        const prev = detectedInMeasure[detectedInMeasure.length - 1];
        if (prev.durationTicks) {
          prev.durationTicks = nextCTick - (prev.startTick ?? measureStartTick);
        }
        continue;
      }

      // Check for consecutive duplicate chords (e.g. repeated comping quarter notes on the same chord)
      if (detectedInMeasure.length > 0) {
        const prev = detectedInMeasure[detectedInMeasure.length - 1];
        if (prev.symbol === chord.symbol) {
          // Merge: extend previous chord
          prev.durationTicks = nextCTick - (prev.startTick ?? measureStartTick);
          continue;
        }
      }

      // Snap chord start tick to closest beat if within 160 ticks
      let snappedStart = cTick;
      const offsetInBar = cTick - measureStartTick;
      const nearestBeat = Math.round(offsetInBar / beatTicks);
      const beatTick = measureStartTick + nearestBeat * beatTicks;
      if (Math.abs(cTick - beatTick) <= 160 && beatTick < measureEndTick) {
        snappedStart = beatTick;
      }

      chord.startTick = snappedStart;
      chord.durationTicks = Math.max(beatTicks, nextCTick - snappedStart);
      detectedInMeasure.push(chord);
    }

    // Arpeggio fallback: tight onset-clustering is tuned for block/comping chords. A chord
    // played as a broken/arpeggiated line (notes spread across the bar, little overlap)
    // ends up as several single-note fragments instead of one harmony. If nothing richer
    // than single notes was found, re-analyze every note in the measure together as one
    // sustained chord (duration/velocity weighting already tolerates the spread timing).
    const hasRealChord = detectedInMeasure.some(
      (c) => c.suffix !== '' || (c.pitches && new Set(c.pitches.map((p) => p % 12)).size >= 3)
    );
    if (!hasRealChord && notesInMeasure.length >= 2) {
      const wholeMeasureChord = detectChordFromNotes(notesInMeasure, measureStartTick, useFlats);
      if (wholeMeasureChord && (wholeMeasureChord.suffix !== '' || detectedInMeasure.length === 0)) {
        wholeMeasureChord.startTick = measureStartTick;
        wholeMeasureChord.durationTicks = measureTicks;
        detectedInMeasure.length = 0;
        detectedInMeasure.push(wholeMeasureChord);
      }
    }

    // Final clean-up and duration normalization
    if (detectedInMeasure.length === 1) {
      // Single chord in measure spans the entire bar
      detectedInMeasure[0].startTick = measureStartTick;
      detectedInMeasure[0].durationTicks = measureTicks;
    } else if (detectedInMeasure.length > 1) {
      // First chord starts at measure start if near beat 1
      if ((detectedInMeasure[0].startTick ?? 0) - measureStartTick < beatTicks * 0.6) {
        detectedInMeasure[0].startTick = measureStartTick;
      }
      // Ensure each chord ends cleanly where the next begins, and the last ends at measureEndTick
      for (let i = 0; i < detectedInMeasure.length; i++) {
        const cur = detectedInMeasure[i];
        const next = i < detectedInMeasure.length - 1 ? detectedInMeasure[i + 1] : null;
        const curStart = cur.startTick ?? measureStartTick;
        const nextStart = next ? (next.startTick ?? measureEndTick) : measureEndTick;
        cur.durationTicks = Math.max(120, nextStart - curStart);
      }
    }

    if (detectedInMeasure.length > 0) {
      result.set(m, detectedInMeasure);
    }
  }

  return result;
}

/**
 * Convert a chord symbol back to MIDI pitches for auditory preview/audition
 */
export function chordSymbolToPitches(chord: DetectedChord, baseOctave: number = 4): number[] {
  const rootIndex = PITCH_NAMES_SHARP.indexOf(chord.root);
  if (rootIndex === -1) return [60, 64, 67]; // default C major

  const rootMidi = 12 * baseOctave + rootIndex;
  const def = CHORD_DEFINITIONS.find((d) => d.suffix === chord.suffix) || CHORD_DEFINITIONS[0];

  const chordPitches = def.intervals.map((iv) => rootMidi + iv);

  // If there's a slash bass note, add it in octave below
  if (chord.bass && chord.bass !== chord.root) {
    const bassIndex = PITCH_NAMES_SHARP.indexOf(chord.bass);
    if (bassIndex !== -1) {
      const bassMidi = 12 * (baseOctave - 1) + bassIndex;
      return [bassMidi, ...chordPitches];
    }
  }

  return chordPitches;
}
